import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Constants } from "../../../constants";
import type { INotification } from "../../interfaces";
import DataService from "./data.service";
import DialogService from "./dialog.service";
import ExceptionService from "./exception.service";
import { ArcusAuthService } from "../../core/arcusauth.service";

@Injectable()
export default class NotificationService {
    static readonly FRAUD_METADATA_KEY = "isFraud";

    intervalDelay: number = 60000;
    service: NodeJS.Timeout;
    desktopNotifications: boolean;
    private notificationSource = new BehaviorSubject<INotification[]>([]);
    notifications$ = this.notificationSource.asObservable();
    public notificationCount = 0;

    private fraudAlertSource = new BehaviorSubject<INotification[]>([]);
    fraudAlerts$ = this.fraudAlertSource.asObservable();
    public fraudAlertCount = 0;

    constructor(
        private dataService: DataService,
        private exceptionService: ExceptionService,
        private dialogService: DialogService,
        private authService: ArcusAuthService
    ) {
        try {
            this.desktopNotifications = Notification && Notification.permission === "granted";
            this.authService.isAuthenticated$.subscribe((ready) => {
                if (ready) {
                    this.startService();
                } else {
                    this.stopService();
                }
            });
        } catch (err) {
            // this is to catch if ios device and not have Notification Api
            // also in case something goes wrong we don't want the site not to load, just becasue notifications won't work
            console.error(err);
            this.desktopNotifications = false;
        }
    }

    isIOS() {
        const browserInfo = navigator.userAgent.toLowerCase();

        if (browserInfo.match("iphone") || browserInfo.match("ipad")) {
            return true;
        }
        if (
            ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
                navigator.platform
            ) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
        ) {
            return true;
        }
        return false;
    }

    isAndroid() {
        return /android/i.test(navigator.userAgent);
    }

    startService = (): void => {
        if (this.service) {
            return; // console.warn("Notification service already started", this.service);
        }
        this.checkDesktopNotifications();
        this.getNotifications();
        this.service = setInterval(() => this.getNotifications(), this.intervalDelay);
    };

    stopService = (): void => {
        clearInterval(this.service);
        this.service = undefined;
    };

    getNotifications = () => {
        this.dataService
            .getApi<INotification[]>("notification")
            .then((result) => {
                if (result) {
                    const fraudAlerts = result.filter(
                        (x) => x.Metadata?.[NotificationService.FRAUD_METADATA_KEY] === "true"
                    );

                    if (result.length > this.notificationCount) {
                        if (this.desktopNotifications) {
                            this.createDesktopNotification();
                        }
                    }

                    this.notificationSource.next(result);
                    this.notificationCount = result.length;

                    this.fraudAlertSource.next(fraudAlerts);
                    this.fraudAlertCount = fraudAlerts.length;
                }
            })
            .catch((error) => {
                if (error?.error === "login_required" || error?.message === "Login required") {
                    this.stopService();
                    return;
                }
                // Silently skip this poll tick, next interval will retry
                if (error?.status === 0 || error?.status === 503 || error?.status === 504) {
                    return;
                }
                this.exceptionService.handleException(error);
            });
    };

    dismissNotification = (notification: INotification) => {
        this.dataService
            .put(`api/Notification/${notification.ID}`, null)
            .then(() => {
                const updated = this.notificationSource.getValue().filter((x) => x.ID != notification.ID);
                this.notificationSource.next(updated);
                this.notificationCount = updated.length;

                const updatedFraud = updated.filter(
                    (x) => x.Metadata?.[NotificationService.FRAUD_METADATA_KEY] === "true"
                );
                this.fraudAlertSource.next(updatedFraud);
                this.fraudAlertCount = updatedFraud.length;
            })
            .catch((error) => this.exceptionService.handleException(error));
    };

    checkDesktopNotifications = (): void => {
        if (this.isIOS()) {
            this.desktopNotifications = false;
            return;
        }
        if (!this.desktopNotifications && Notification && Notification.permission !== "denied") {
            Notification.requestPermission((result) => {
                this.desktopNotifications = result === "granted";
            });
        }
    };

    createDesktopNotification = (): void => {
        if (!this.desktopNotifications) {
            return;
        }

        const title = "New notification received";
        const options = {
            body: "You have received a new notification in Arcus.",
            icon: Constants.baseURL + "assets/images/arcus-logo-vertical.png",
        };

        // Android Chrome does not support the Notification constructor — must use Service Worker API
        if (this.isAndroid() && navigator.serviceWorker) {
            navigator.serviceWorker.ready
                .then((registration) => registration.showNotification(title, options))
                .catch((err) => console.error(err));
            return;
        }

        try {
            const desktopNotification = new Notification(title, options);
            desktopNotification.onclick = (e: Event) => {
                window.focus();
                (e.target as Notification).close();
            };
        } catch (err) {
            console.error(err);
        }
    };
}
